import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnInit,
    ChangeDetectorRef,
    ViewEncapsulation,
    ChangeDetectionStrategy,
} from "@angular/core";

import { FormsModule } from "@angular/forms";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { Workflow as WorkflowTypes } from "./service/Workflow";
import { S25DropdownPaginatedModule } from "../s25-dropdown/s25.dropdown.paginated.module";
import { DropDownItem } from "../../pojo/DropDownItem";
import { ContactService } from "../../services/contact.service";
import { SearchCriteriaService } from "../../services/search/search-criteria/search.criteria.service";
import { S25EditableTextComponent } from "../s25-editable/s25-editable-text/s25.editable.text.component";
import { S25CheckboxModule } from "../s25-checkbox/s25.checkbox.module";
import { S25RadioModule } from "../s25-radio/s25.radio.module";
import { S25IconComponent } from "../s25-icon/s25.icon.component";
import { S25InfoMessageModule } from "../s25-info-message/s25.info.message.module";

@TypeManagerDecorator("workflow-states")
@Component({
    selector: "workflow-states",
    template: `
        @if (selectedStage) {
            <div class="states-header">
                <h3>Approvals for {{ selectedStage.stageName }}</h3>
                <button class="aw-button aw-button--primary" (click)="addState()">Add Approval</button>
            </div>

            @if (selectedStage.hasObjectApprovals) {
                <div class="object-approvals-banner">
                    <s25-ng-info-message [isPopover]="false">
                        In addition to any approvals configured below, approval states are automatically generated for
                        each location and resource in the form's occurrences. Approvers are determined by 25Live
                        security group permissions on each location or resource.
                    </s25-ng-info-message>
                </div>
            }

            <div class="states-list">
                @for (state of selectedStage.states || []; track state.workflowStateId || state.id) {
                    <div class="state-item">
                        <div class="workflow-header">
                            <div class="workflow-info">
                                <s25-ng-editable-text
                                    [val]="state.stateName"
                                    [hasCommit]="true"
                                    [hasCommitButton]="true"
                                    [hasCancelButton]="true"
                                    [customValidation]="getStateNameValidation(state)"
                                    [onCommit]="getStateNameCommitHandler(state)"
                                    placeholder="Approval name"
                                    class="state-name"
                                ></s25-ng-editable-text>
                                <div class="state-flags">
                                    <s25-ng-checkbox
                                        [value]="state.defaultEnabled"
                                        (valueChange)="updateStateDefaultEnabled(state, $event)"
                                        >Enabled
                                        <s25-ng-info-message [isPopover]="true"
                                            >This approval can be manually enabled or disabled here, but approval rules
                                            may take precedence based on specific conditions. To review or modify these
                                            rules, see <b>Approval Rules</b> in the Approvals step below.
                                        </s25-ng-info-message>
                                    </s25-ng-checkbox>
                                    @if (aiFeatureEnabled) {
                                        <s25-ng-checkbox
                                            [value]="!!state.hasAiApprover"
                                            (valueChange)="updateAiApprover(state, $event)"
                                            ><s25-ng-icon
                                                [type]="'aiSparkles'"
                                                [ariaLabel]="false"
                                                class="ai-sparkles-adjacent"
                                            />
                                            AI Approver
                                            <s25-ng-info-message [isPopover]="true"
                                                >When enabled, an AI reviewer will evaluate submissions against the
                                                description below before human approvers are assigned. Human approvers
                                                serve as fallback if AI is uncertain.
                                            </s25-ng-info-message>
                                        </s25-ng-checkbox>
                                    }
                                </div>
                            </div>
                            <div class="state-actions">
                                <button
                                    class="btn btn-sm"
                                    (click)="toggleStateCollapse(state)"
                                    [style.padding-inline]="'11px'"
                                >
                                    {{ isStateCollapsed(state) ? "▼" : "▲" }}
                                </button>
                                <button
                                    class="btn btn-sm btn-danger"
                                    (click)="deleteState(state, $event)"
                                    aria-label="Remove state"
                                >
                                    <s25-ng-icon [type]="'close'" [ariaLabel]="false" />
                                </button>
                            </div>
                        </div>

                        @if (!isStateCollapsed(state)) {
                            <div class="state-details">
                                <div class="state-description">
                                    <textarea
                                        [(ngModel)]="state.description"
                                        (ngModelChange)="onStateChange()"
                                        [placeholder]="
                                            state.hasAiApprover
                                                ? 'AI review instructions (e.g., criteria the submission must meet)'
                                                : 'Description (optional)'
                                        "
                                        rows="2"
                                    ></textarea>
                                    @if (state.hasAiApprover) {
                                        <p class="ai-description-note">
                                            When AI Approver is enabled, the description above is used as instructions
                                            for the AI reviewer. Human approvers serve as fallback if AI is uncertain.
                                        </p>
                                        <div class="ai-capabilities">
                                            <label class="section-label ai-assisted-label">
                                                <s25-ng-icon
                                                    [type]="'aiSparkles'"
                                                    [ariaLabel]="false"
                                                    class="ai-sparkles-adjacent"
                                                />
                                                AI can:
                                            </label>
                                            <div class="ai-capabilities-row">
                                                <s25-ng-checkbox
                                                    [value]="state.aiCanApprove !== false"
                                                    (valueChange)="updateAiCapability(state, 'aiCanApprove', $event)"
                                                    >Approve</s25-ng-checkbox
                                                >
                                                <s25-ng-checkbox
                                                    [value]="state.aiCanDeny !== false"
                                                    (valueChange)="updateAiCapability(state, 'aiCanDeny', $event)"
                                                    >Deny</s25-ng-checkbox
                                                >
                                                <s25-ng-checkbox
                                                    [value]="state.aiCanRequestChanges !== false"
                                                    (valueChange)="
                                                        updateAiCapability(state, 'aiCanRequestChanges', $event)
                                                    "
                                                    >Request Changes</s25-ng-checkbox
                                                >
                                            </div>
                                        </div>
                                        @if (!hasContactsAssigned(state)) {
                                            <p class="ai-no-fallback-warning">
                                                No human fallback approvers configured. If AI is uncertain about a
                                                submission, there will be no one to review it.
                                            </p>
                                        }
                                    }
                                </div>

                                <div class="contact-assignment">
                                    <h4>Contacts</h4>
                                    <div class="contact-mode">
                                        <s25-ng-checkbox
                                            [value]="hasDirectContactsEnabled(state)"
                                            (valueChange)="setDirectContactsEnabled(state, $event)"
                                            >Direct Contacts</s25-ng-checkbox
                                        >
                                        <s25-ng-checkbox
                                            [value]="hasApprovalGroupEnabled(state)"
                                            (valueChange)="setApprovalGroupEnabled(state, $event)"
                                            >Approval Group</s25-ng-checkbox
                                        >
                                        <s25-ng-checkbox
                                            [value]="hasOrgRolesEnabled(state)"
                                            (valueChange)="setOrgRolesEnabled(state, $event)"
                                            >Organization Roles</s25-ng-checkbox
                                        >
                                    </div>

                                    @if (hasDirectContactsEnabled(state)) {
                                        <div class="contact-section">
                                            <label class="section-label">Direct Contacts:</label>
                                            <div class="contacts-list">
                                                @for (contact of state.contacts || []; track contact.contId) {
                                                    <div class="contact-item">
                                                        <span>{{
                                                            contact.contactName || "Contact " + contact.contId
                                                        }}</span>
                                                        <button
                                                            class="aw-button aw-button--danger--transparent"
                                                            (click)="removeContact(state, contact)"
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>
                                                }
                                                <s25-contact-dropdown
                                                    [value]="getContactDropdownSelection(state)"
                                                    (valueChange)="onContactSelected(state, $event)"
                                                    [resetSelectedOnCleanup]="true"
                                                    [ariaLabel]="'Direct Contacts'"
                                                />
                                            </div>
                                        </div>
                                    }

                                    @if (hasApprovalGroupEnabled(state)) {
                                        <div class="contact-section">
                                            <label class="section-label">Approval Group:</label>
                                            <div class="approval-group">
                                                <s25-approval-group-dropdown
                                                    [value]="getApprovalGroupForState(state)"
                                                    (valueChange)="onApprovalGroupChange(state, $event)"
                                                    [noneItemLabel]="'No Approval Group'"
                                                />
                                            </div>
                                        </div>
                                    }

                                    @if (hasOrgRolesEnabled(state)) {
                                        <div class="contact-section">
                                            <label class="section-label">Organization Roles:</label>
                                            <div class="org-roles-list">
                                                @for (orgRole of state.orgRoles || []; track orgRole.contRoleId) {
                                                    <div class="contact-item">
                                                        <span>{{ getOrgRoleName(orgRole.contRoleId) }}</span>
                                                        <button
                                                            class="aw-button aw-button--danger--transparent"
                                                            (click)="removeOrgRole(state, orgRole)"
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>
                                                }
                                                <s25-ng-dropdown-search-criteria
                                                    [type]="'organizationRoles'"
                                                    [value]="getOrgRoleDropdownSelection(state)"
                                                    (valueChange)="onOrgRoleSelected(state, $event)"
                                                    [placeholder]="'Select an Organization Role'"
                                                    [resetSelectedOnCleanup]="true"
                                                />
                                            </div>
                                        </div>
                                    }
                                </div>
                            </div>
                        }
                    </div>
                }
            </div>
        } @else {
            <div class="no-stage">
                <p>Select a stage to manage its approvals.</p>
            </div>
        }
    `,
    styles: `
        :host {
            padding: 20px;
        }

        .states-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 20px;
        }

        .states-header h3 {
            margin: 0;
            color: var(--color--text-strong);
        }

        .btn-danger {
            background: var(--color--danger);
            color: white;
        }

        .btn-danger:hover {
            background: var(--color--danger);
            opacity: 0.9;
        }

        .states-list {
            display: flex;
            flex-direction: column;
            gap: 16px;
        }

        .state-item {
            background: var(--color--surface-bg);
            border: 1px solid var(--color--border);
            border-radius: 8px;
            overflow: hidden;
        }

        .state-name {
            font-weight: 500;
            color: var(--color--text-strong);
        }

        .state-flags {
            display: flex;
            gap: 16px;
        }

        .state-actions {
            display: flex;
            gap: 8px;
        }

        .state-details {
            padding: 20px;
            border-top: 1px solid var(--color--border);
        }

        .state-description {
            margin-bottom: 20px;
        }

        .state-description textarea {
            width: 100%;
            padding: 8px 12px;
            border: 1px solid var(--color--border-bold);
            border-radius: 4px;
            font-size: 14px;
            resize: vertical;
        }

        .contact-assignment {
            margin-bottom: 20px;
        }

        .contact-assignment h4 {
            margin: 0 0 12px 0;
            color: var(--color--text-strong);
            font-size: 14px;
            font-weight: 500;
        }

        /* Fix dropdown positioning */
        :host ::ng-deep .ngDropdownDrop {
            position: relative !important;
        }

        .contact-mode {
            display: flex;
            gap: 16px;
            margin-bottom: 20px;
        }

        .contact-section {
            margin-top: 20px;
        }

        .contact-section:first-of-type {
            margin-top: 0;
        }

        .section-label {
            display: block;
            font-weight: 500;
            color: var(--color--text);
            font-size: 13px;
            margin-bottom: 8px;
        }

        .ai-assisted-label {
            display: inline-flex;
            align-items: center;
            gap: 0.35em;
        }

        .ai-sparkles-adjacent {
            flex-shrink: 0;
        }

        .approval-group,
        .contacts-list,
        .org-roles-list {
            margin-top: 0;
        }

        .contact-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 8px 12px;
            background: var(--color--surface-2-bg);
            border: 1px solid var(--color--border);
            border-radius: 4px;
            margin-bottom: 8px;
        }

        .object-approvals-banner {
            padding: 16px 20px;
            background: var(--color--surface-2-bg);
            border: 1px solid var(--color--border);
            border-radius: 8px;
            color: var(--color--text);
            font-size: 14px;
            line-height: 1.5;
        }

        .no-stage {
            text-align: center;
            padding: 40px 20px;
            color: var(--color--text-muted);
        }

        s25-ng-checkbox {
            font-size: 0.9rem;
        }

        s25-ng-info-message {
            position: relative;
            bottom: 1px;
            right: 2px;
        }

        .ai-description-note {
            margin: 8px 0 0;
            font-size: 12px;
            color: var(--color--text-muted);
            font-style: italic;
        }

        .ai-capabilities {
            margin: 8px 0 0;
        }

        .ai-capabilities-row {
            display: flex;
            gap: 16px;
            margin-top: 4px;
        }

        .ai-no-fallback-warning {
            margin: 4px 0 0;
            font-size: 12px;
            color: var(--color--danger);
            font-weight: 500;
        }

        ::ng-deep s25-ng-checkbox span {
            white-space: nowrap;
        }

        .workflow-header {
            padding: 16px;
            display: flex;
            justify-content: space-between;
            align-items: center;
            background: var(--color--surface-2-bg);
        }

        .workflow-header:has(s25-ng-editable-text.editing) {
            align-items: flex-start;
        }

        .workflow-info {
            display: flex;
            flex-direction: column;
            font-size: 0.9rem;
        }

        .workflow-info:has(s25-ng-editable-text.editing) {
            flex: 1;
            gap: 5px;
        }

        .workflow-info s25-ng-editable-text.editing input {
            width: 95%;
        }
    `,
    standalone: true,
    imports: [
        FormsModule,
        S25DropdownPaginatedModule,
        S25EditableTextComponent,
        S25CheckboxModule,
        S25RadioModule,
        S25IconComponent,
        S25InfoMessageModule,
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    encapsulation: ViewEncapsulation.Emulated,
})
export class WorkflowStatesComponent implements OnInit {
    @Input() workflowProcess!: WorkflowTypes.WorkflowProcess;
    @Input() selectedStage: WorkflowTypes.WorkflowStage | null = null;
    @Input() aiFeatureEnabled = false;
    @Output() statesChange = new EventEmitter<WorkflowTypes.WorkflowState[]>();

    private collapsedStates = new Set<number | string>();
    private selectedApprovalGroupsForStates = new Map<number | string, DropDownItem | null>();
    private contactDropdownSelections = new Map<number | string, DropDownItem | null>();
    private orgRoleDropdownSelections = new Map<number | string, DropDownItem | null>();
    private orgRoleNameMap = new Map<number, string>();
    private directContactsEnabled = new Map<number | string, boolean>();
    private approvalGroupEnabled = new Map<number | string, boolean>();
    private orgRolesEnabled = new Map<number | string, boolean>();
    private nextNegativeId = -1;

    constructor(private cd: ChangeDetectorRef) {}

    async ngOnInit() {
        if (!this.selectedStage?.states) return;

        for (const state of this.selectedStage.states) {
            const stateKey = state.workflowStateId || state.id!;

            // Initialize boolean flags based on existing data
            this.directContactsEnabled.set(stateKey, !!(state.contacts && state.contacts.length > 0));
            this.approvalGroupEnabled.set(stateKey, !!state.approvalGroupId);
            this.orgRolesEnabled.set(stateKey, !!(state.orgRoles && state.orgRoles.length > 0));

            // Initialize approval group selection if state has an approval group ID
            if (state.approvalGroupId) {
                // Create a placeholder DropDownItem - the actual name will be loaded by the dropdown component
                this.selectedApprovalGroupsForStates.set(stateKey, {
                    itemId: state.approvalGroupId,
                    itemName: `Loading...`, // This will be replaced when the dropdown loads
                    itemTypeId: undefined,
                } as DropDownItem);
            }

            // Initialize orgRoles if not present
            if (!state.orgRoles) {
                state.orgRoles = [];
            }

            // Initialize contacts array if not present
            if (!state.contacts) {
                state.contacts = [];
            }
        }

        const contIds = this.selectedStage.states.flatMap((s) => s.contacts?.map((c) => c.contId) || []);

        try {
            if (contIds.length) {
                const contactNames = await ContactService.getContactsNameAndFormal(contIds);
                const contactNameMap = new Map(contactNames.map((c) => [c.itemId, c.itemName]));
                this.selectedStage.states.forEach((s) => {
                    s.contacts?.forEach((c) => (c.contactName = contactNameMap.get(c.contId) || `Contact ${c.contId}`));
                });
            }

            // Fetch all organization roles to build the name map
            const orgRoles = await SearchCriteriaService.getOrganizationRoles();
            const roleNameMap = new Map<number, string>();
            orgRoles.forEach((role) => {
                if (role.itemId && role.itemName) {
                    roleNameMap.set(Number(role.itemId), role.itemName);
                }
            });
            this.orgRoleNameMap = roleNameMap;
        } catch (error) {
            console.error("Error loading contact names or organization roles:", error);
        }

        this.cd.detectChanges();
    }

    updateStateDefaultEnabled(state: WorkflowTypes.WorkflowState, enabled: boolean) {
        state.defaultEnabled = enabled;
        this.onStateChange();
    }

    updateAiApprover(state: WorkflowTypes.WorkflowState, enabled: boolean) {
        state.hasAiApprover = enabled;
        if (enabled) {
            state.aiCanApprove ??= true;
            state.aiCanDeny ??= true;
            state.aiCanRequestChanges ??= true;
        }
        this.onStateChange();
    }

    updateAiCapability(
        state: WorkflowTypes.WorkflowState,
        field: "aiCanApprove" | "aiCanDeny" | "aiCanRequestChanges",
        enabled: boolean,
    ) {
        state[field] = enabled;
        this.onStateChange();
    }

    onStateChange() {
        this.statesChange.emit(this.selectedStage?.states || []);
        this.cd.detectChanges();
    }

    addState() {
        if (!this.selectedStage) {
            console.error("No selected stage for adding state");
            return;
        }

        const nextId = this.getNextNegativeId();
        const newState: WorkflowTypes.WorkflowState = {
            id: nextId,
            workflowProcessId: this.workflowProcess.workflowProcessId,
            workflowStageId: this.selectedStage.workflowStageId,
            stateName: `Approval ${(this.selectedStage.states?.length || 0) + 1}`,
            description: "",
            defaultEnabled: true,
            contacts: [],
            orgRoles: [],
        };

        // Initialize boolean flags for new state (all disabled by default)
        const stateKey = nextId;
        this.directContactsEnabled.set(stateKey, false);
        this.approvalGroupEnabled.set(stateKey, false);
        this.orgRolesEnabled.set(stateKey, false);

        if (!this.selectedStage.states) {
            this.selectedStage.states = [];
        }

        this.selectedStage.states.push(newState);
        this.onStateChange();
    }

    private getNextNegativeId(): number {
        const allStates = this.selectedStage?.states || [];
        return allStates.reduce((minId, state) => Math.min(minId, state.id || -1), -1) - 1;
    }

    deleteState(state: WorkflowTypes.WorkflowState, event: Event) {
        event.stopPropagation();

        if (!this.selectedStage?.states) return;

        const index = this.selectedStage.states.findIndex(
            (s) => (s.workflowStateId && s.workflowStateId === state.workflowStateId) || (s.id && s.id === state.id),
        );

        if (index > -1) {
            this.selectedStage.states.splice(index, 1);
            this.onStateChange();
        }
    }

    toggleStateCollapse(state: WorkflowTypes.WorkflowState) {
        const stateKey = state.workflowStateId || state.id!;

        if (this.collapsedStates.has(stateKey)) {
            this.collapsedStates.delete(stateKey);
        } else {
            this.collapsedStates.add(stateKey);
        }
    }

    isStateCollapsed(state: WorkflowTypes.WorkflowState): boolean {
        const stateKey = state.workflowStateId || state.id!;
        return this.collapsedStates.has(stateKey);
    }

    getStateNameValidation(state: WorkflowTypes.WorkflowState) {
        return (value: string) => {
            if (!value || value.trim().length === 0) {
                return ["Approval name is required"];
            }

            // Check for duplicate state names within the same stage
            const duplicateState = this.selectedStage?.states?.find(
                (s) =>
                    s.stateName === value.trim() &&
                    ((s.workflowStateId && s.workflowStateId !== state.workflowStateId) || (s.id && s.id !== state.id)),
            );

            if (duplicateState) {
                return ["Approval name must be unique within the stage"];
            }

            return true;
        };
    }

    getStateNameCommitHandler(state: WorkflowTypes.WorkflowState) {
        return (value: string) => {
            state.stateName = value;
            this.onStateChange();
        };
    }

    getApprovalGroupForState(state: WorkflowTypes.WorkflowState): DropDownItem | null {
        const stateKey = state.workflowStateId || state.id!;
        return this.selectedApprovalGroupsForStates.get(stateKey) || null;
    }

    onApprovalGroupChange(state: WorkflowTypes.WorkflowState, item: DropDownItem | null) {
        const stateKey = state.workflowStateId || state.id!;
        this.selectedApprovalGroupsForStates.set(stateKey, item);

        if (item && item.itemId) {
            state.approvalGroupId = parseInt(item.itemId.toString());
            this.approvalGroupEnabled.set(stateKey, true);
        } else {
            state.approvalGroupId = undefined;
            this.approvalGroupEnabled.set(stateKey, false);
        }

        this.onStateChange();
    }

    onContactSelected(state: WorkflowTypes.WorkflowState, selectedContact: DropDownItem) {
        if (!selectedContact) return;

        // Store the selection temporarily
        const stateKey = state.workflowStateId || state.id!;
        this.contactDropdownSelections.set(stateKey, selectedContact);

        if (!state.contacts) {
            state.contacts = [];
        }

        // Check if contact is already added
        const existingContact = state.contacts.find((c) => c.contId === Number(selectedContact.itemId));
        if (existingContact) {
            // Reset dropdown even if contact already exists using timeout bc we are currently in the same cycle that set the selection
            setTimeout(() => {
                this.contactDropdownSelections.set(stateKey, null);
                this.cd.detectChanges();
            }, 0);
            return;
        }

        state.contacts.push({
            contId: Number(selectedContact.itemId),
            contactName: selectedContact.itemName,
        });

        // Reset the dropdown selection using a timeout to ensure it happens after the current cycle because we are currently in the same cycle that set the selection
        setTimeout(() => {
            this.contactDropdownSelections.set(stateKey, null);
            this.cd.detectChanges();
        }, 0);

        this.onStateChange();
    }

    getContactDropdownSelection(state: WorkflowTypes.WorkflowState): DropDownItem | null {
        const stateKey = state.workflowStateId || state.id!;
        const selection = this.contactDropdownSelections.get(stateKey);
        return selection === undefined ? null : selection;
    }

    removeContact(state: WorkflowTypes.WorkflowState, contact: WorkflowTypes.WorkflowStateContact) {
        if (!state.contacts) return;
        const index = state.contacts.findIndex((c) => c.contId === contact.contId);
        if (index > -1) {
            state.contacts.splice(index, 1);
            this.onStateChange();
        }
    }

    hasDirectContactsEnabled(state: WorkflowTypes.WorkflowState): boolean {
        const stateKey = state.workflowStateId || state.id!;
        return this.directContactsEnabled.get(stateKey) ?? false;
    }

    setDirectContactsEnabled(state: WorkflowTypes.WorkflowState, enabled: boolean) {
        const stateKey = state.workflowStateId || state.id!;
        this.directContactsEnabled.set(stateKey, enabled);
        if (!enabled) {
            // Clear contacts when unchecked
            state.contacts = [];
            this.contactDropdownSelections.set(stateKey, null);
        }
        this.onStateChange();
    }

    hasApprovalGroupEnabled(state: WorkflowTypes.WorkflowState): boolean {
        const stateKey = state.workflowStateId || state.id!;
        return this.approvalGroupEnabled.get(stateKey) ?? false;
    }

    setApprovalGroupEnabled(state: WorkflowTypes.WorkflowState, enabled: boolean) {
        const stateKey = state.workflowStateId || state.id!;
        this.approvalGroupEnabled.set(stateKey, enabled);
        if (!enabled) {
            // Clear approval group when unchecked
            state.approvalGroupId = undefined;
            this.selectedApprovalGroupsForStates.set(stateKey, null);
        }
        this.onStateChange();
    }

    hasOrgRolesEnabled(state: WorkflowTypes.WorkflowState): boolean {
        const stateKey = state.workflowStateId || state.id!;
        return this.orgRolesEnabled.get(stateKey) ?? false;
    }

    setOrgRolesEnabled(state: WorkflowTypes.WorkflowState, enabled: boolean) {
        const stateKey = state.workflowStateId || state.id!;
        this.orgRolesEnabled.set(stateKey, enabled);
        if (!enabled) {
            // Clear org roles when unchecked
            state.orgRoles = [];
            this.orgRoleDropdownSelections.set(stateKey, null);
        }
        this.onStateChange();
    }

    hasContactsAssigned(state: WorkflowTypes.WorkflowState): boolean {
        return (
            !!(state.contacts && state.contacts.length > 0) ||
            !!state.approvalGroupId ||
            !!(state.orgRoles && state.orgRoles.length > 0)
        );
    }

    getContactCount(state: WorkflowTypes.WorkflowState): number {
        let count = 0;
        count += state.contacts?.length ?? 0;
        if (state.approvalGroupId) {
            count += 1; // Approval groups count as 1 contact assignment
        }
        count += state.orgRoles?.length ?? 0;
        return count;
    }

    onOrgRoleSelected(state: WorkflowTypes.WorkflowState, selectedRole: DropDownItem) {
        if (!selectedRole) return;

        const stateKey = state.workflowStateId || state.id!;
        this.orgRoleDropdownSelections.set(stateKey, selectedRole);

        if (!state.orgRoles) {
            state.orgRoles = [];
        }

        // Check if role is already added
        const roleId = Number(selectedRole.itemId);
        if (state.orgRoles.some((orgRole) => orgRole.contRoleId === roleId)) {
            // Reset dropdown even if role already exists
            setTimeout(() => {
                this.orgRoleDropdownSelections.set(stateKey, null);
                this.cd.detectChanges();
            }, 0);
            return;
        }

        state.orgRoles.push({
            contRoleId: roleId,
        });

        // Reset the dropdown selection
        setTimeout(() => {
            this.orgRoleDropdownSelections.set(stateKey, null);
            this.cd.detectChanges();
        }, 0);

        this.onStateChange();
    }

    getOrgRoleDropdownSelection(state: WorkflowTypes.WorkflowState): DropDownItem | null {
        const stateKey = state.workflowStateId || state.id!;
        const selection = this.orgRoleDropdownSelections.get(stateKey);
        return selection === undefined ? null : selection;
    }

    removeOrgRole(state: WorkflowTypes.WorkflowState, orgRole: WorkflowTypes.WorkflowStateOrgRole) {
        if (!state.orgRoles) return;
        const index = state.orgRoles.findIndex((r) => r.contRoleId === orgRole.contRoleId);
        if (index > -1) {
            state.orgRoles.splice(index, 1);
            this.onStateChange();
        }
    }

    getOrgRoleName(roleId: number): string {
        return this.orgRoleNameMap.get(roleId) || `Role ${roleId}`;
    }
}
